import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { MarketEvent, NewsItem, ResearchCase, WatchlistItem } from '../api/client'
import { useItemQuery } from '../hooks/useItemQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { EmptyState, Loading } from '../components/common/Feedback'
import { useHasPermission } from '../api/session'
import { GruendeDialog, RuhenDialog } from './WatchlistDialogs'
import { grundName } from './watchlistVocabulary'
import s from './WatchlistDetail.module.scss'

/**
 * Was zu einem beobachteten Titel vorliegt: Meldungen, Termine, Vorgänge.
 *
 * DREI ABFRAGEN UND NICHT EINE. Die drei Endpunkte gibt es getrennt, und ein
 * gemeinsamer wäre ein vierter, der nur diesem Bereich diente. Sie laufen
 * nebeneinander; jeder Abschnitt erscheint, sobald seine Antwort da ist —
 * niemand wartet auf den langsamsten.
 *
 * FÜNF ZEILEN JE ABSCHNITT. Ein Detailbereich, der zwanzig Meldungen zeigt,
 * ist keine Übersicht mehr. Wer mehr will, folgt dem Verweis in die volle
 * Liste — mit vorgesetztem Filter, damit dort dasselbe steht.
 *
 * LEERE ABSCHNITTE BLEIBEN STEHEN. Dass es zu einem Titel nichts gibt, ist
 * eine Auskunft; ein verschwundener Block sähe aus wie ein Ladefehler.
 */

const MAX_ZEILEN = 5

const datum = new Intl.DateTimeFormat('de-DE', { day: '2-digit',
                                                 month: '2-digit',
                                                 year: '2-digit' })
const tag = (iso: string | null | undefined) =>
  iso ? datum.format(new Date(iso)) : '—'

/** Die Bewertung als Zahl mit Vorzeichen — knapp, für die schmale Spalte. */
function Stimmung({ wert }: { wert: number }) {
  const klasse = wert > 0 ? s.positiv : wert < 0 ? s.negativ : s.neutral
  return <span className={`${s.stimmung} ${klasse}`}>
    {wert > 0 ? `+${wert}` : wert}
  </span>
}

function Abschnitt({ titel, anzahl, mehr, leer, children }: {
  titel: string
  anzahl: number | undefined
  /** Ziel für „alle …" — mit vorgesetztem Filter. */
  mehr?: { pfad: string; text: string }
  leer: string
  children: React.ReactNode
}) {
  return (
    <Card title={titel} span={12} flush
          meta={anzahl !== undefined ? `${anzahl}` : undefined}>
      {anzahl === 0
        ? <div className={s.leer}>{leer}</div>
        : <>
            <div className={s.liste}>{children}</div>
            {mehr && anzahl !== undefined && anzahl > MAX_ZEILEN && (
              <div className={s.mehr}>
                <Link to={mehr.pfad}>{mehr.text} ▸</Link>
              </div>
            )}
          </>}
    </Card>
  )
}

export function WatchlistDetail({ eintrag, onGeaendert }: {
  eintrag: WatchlistItem
  /** Die Liste neu laden — ein ruhender Titel fällt aus der Arbeitsliste. */
  onGeaendert: () => void
}) {
  const isin = eintrag.isin
  const darfSchreiben = useHasPermission('watchlist:write')
  const [dialog, setDialog] = useState<'gruende' | 'ruhen' | null>(null)

  // `useCallback` mit der ISIN als Abhängigkeit: Ohne das wäre die
  // Ladefunktion bei jedem Rendern eine neue, und `useItemQuery` lüde
  // endlos nach.
  const ladeNews = useCallback(
    (signal: AbortSignal) =>
      // Als Liste, seit der Filter mehrere Ziele annimmt — hier ist es
      // genau eines.
      api.news({ isin: [isin], size: MAX_ZEILEN, total: true }, signal),
    [isin])
  const ladeTermine = useCallback(
    (signal: AbortSignal) => api.marketEvents(
      { ref: `isin:${isin}`, size: MAX_ZEILEN, total: true }, signal), [isin])
  const ladeVorgaenge = useCallback(
    (signal: AbortSignal) =>
      api.researchCases({ isin, size: MAX_ZEILEN, total: true }, signal), [isin])

  const meldungen = useItemQuery(ladeNews)
  const termine = useItemQuery(ladeTermine)
  const vorgaenge = useItemQuery(ladeVorgaenge)

  return (
    <Card span={12} flush>
      <div className={s.kopf}>
        {/*
          Der Sprung zum Instrument stand vorher auf der Zeile selbst. Er
          bleibt möglich — nur nicht mehr als Nebenwirkung eines Klicks, der
          etwas anderes zu tun scheint.
        */}
        <Link to={`/instruments/${eintrag.instrument_id}`} className={s.titel}>
          {eintrag.name}
        </Link>
        <span className={s.kennung}>{eintrag.isin}</span>
        <span className={s.platz}>{eintrag.primary_exchange}</span>
        <Link to={`/instruments/${eintrag.instrument_id}`} className={s.sprung}>
          zum Titel ↗
        </Link>

        {/*
          Die Pflege sitzt HIER und nicht als Menü an der Zeile: Die Zeile
          klappt schon auf Klick auf, und ein zweites Ziel darin wäre ein
          Klick, der mal das eine und mal das andere tut.
        */}
        {darfSchreiben && (
          <div className={s.aktionen}>
            <Button onClick={() => setDialog('gruende')}>Gründe ändern</Button>
            <Button variant={eintrag.active ? 'danger' : 'primary'}
                    onClick={() => setDialog('ruhen')}>
              {eintrag.active ? 'Ruhen lassen' : 'Wieder aufnehmen'}
            </Button>
          </div>
        )}
      </div>

      {/* Die Gründe stehen sonst nur in der Tabellenzeile — die ist beim
          aufgeklappten Detail oft schon aus dem Blick gescrollt. */}
      <div className={s.gruende}>
        {eintrag.reasons.map(grundName).join(' · ')}
        {!eintrag.active && <span className={s.ruht}>ruhend</span>}
      </div>

      {/*
        NEWS BREIT, die beiden anderen schmal und gestapelt. Eine
        Nachrichtenüberschrift braucht die Breite; ein Termin und ein Vorgang
        sind mit Datum und Status beschrieben. Drei gleich breite Spalten
        hätten links Umbrüche und rechts Leerraum.
      */}
      <div className={s.raster}>
        <div className={s.breit}>
          <Abschnitt titel="News" anzahl={meldungen.item?.total ?? undefined}
                     leer="Keine Meldungen zu diesem Titel."
                     mehr={{ pfad: `/news?isin=${isin}`,
                             text: `alle ${meldungen.item?.total ?? 0}` }}>
            {meldungen.loading && <Loading />}
            {meldungen.item?.items.map((n: NewsItem) => (
              <div key={n.id} className={s.zeile}>
                <span className={s.datum}>{tag(n.published_at)}</span>
                <span className={s.text}>{n.headline}</span>
                <Stimmung wert={n.sentiment} />
              </div>
            ))}
          </Abschnitt>
        </div>

        <div className={s.schmal}>
          <Abschnitt titel="Ereignisse" anzahl={termine.item?.total ?? undefined}
                     leer="Keine Termine eingetragen."
                     mehr={{ pfad: `/events?ref=isin:${isin}`,
                             text: `alle ${termine.item?.total ?? 0}` }}>
            {termine.loading && <Loading />}
            {termine.item?.items.map((e: MarketEvent) => (
              <div key={e.id} className={s.zeile}>
                <span className={s.datum}>{tag(e.date_from)}</span>
                <span className={s.text}>{e.title}</span>
              </div>
            ))}
          </Abschnitt>

          <Abschnitt titel="Cases" anzahl={vorgaenge.item?.total ?? undefined}
                     leer="Keine Vorgänge erfasst."
                     mehr={{ pfad: `/research?isin=${isin}`,
                             text: `alle ${vorgaenge.item?.total ?? 0}` }}>
            {vorgaenge.loading && <Loading />}
            {vorgaenge.item?.items.map((c: ResearchCase) => (
              <div key={c.id} className={s.zeile}>
                <span className={s.datum}>{tag(c.opened_at)}</span>
                <span className={s.text}>{c.title}</span>
              </div>
            ))}
          </Abschnitt>
        </div>
      </div>

      {/*
        EIN Hinweis für alle drei, nicht drei einzelne: Fällt die Verbindung
        aus, fallen sie gemeinsam aus, und dreimal derselbe Satz wäre Lärm.
      */}
      {(meldungen.error || termine.error || vorgaenge.error) && (
        <div className={s.fehler}>
          Zu diesem Titel konnte nicht alles geladen werden.
        </div>
      )}

      {dialog === 'gruende' && (
        <GruendeDialog eintrag={eintrag}
                       onFertig={() => { setDialog(null); onGeaendert() }}
                       onAbbrechen={() => setDialog(null)} />
      )}
      {dialog === 'ruhen' && (
        <RuhenDialog eintrag={eintrag}
                     onFertig={() => { setDialog(null); onGeaendert() }}
                     onAbbrechen={() => setDialog(null)} />
      )}
    </Card>
  )
}

/** Nur damit die leere Ansicht denselben Rahmen hat wie die gefüllte. */
export function WatchlistDetailLeer() {
  return <EmptyState title="nichts ausgewählt">
    Eine Zeile anklicken, um Meldungen, Termine und Vorgänge zu sehen.
  </EmptyState>
}
